/**
 * The running version next to the current one. The reason the scan list exists:
 * "6.1.1 / 6.9.1" next to a red mark is the entire pitch, and it is visible
 * without opening anything.
 *
 * The gap classes stay the scanner's own (`GAP_CLASS` in `lib/siteScan`) rather
 * than becoming `StatusPill` tones. A version gap has six levels - major, minor,
 * patch, current, ahead, unknown - and the design's pill has five; squeezing six
 * meanings into five tones would make "one patch behind" and "two majors behind"
 * the same colour, which is the one distinction this cell is for.
 */
import { AlertTriangle } from "lucide-react";
import { cn } from "@wa/ui";
import { GAP_CLASS, type ScanListItem, type VersionGap } from "../../../../lib/siteScan";
import type { T } from "../types";

/** Version gap ordering for the badge label, worst first. */
const GAP_LABEL: Record<VersionGap, string> = {
  major: "scanner.gap.major",
  minor: "scanner.gap.minor",
  patch: "scanner.gap.patch",
  current: "scanner.gap.current",
  ahead: "scanner.gap.current",
  unknown: "scanner.gap.unknown",
};

/**
 * The row stores the numbers, not the verdict, so the badge is derived here from
 * the same rule the API uses: a different major is a major gap.
 */
export function versionGap(current: string | null | undefined, latest: string | null | undefined): VersionGap {
  if (!current || !latest) return "unknown";
  const c = current.split(".").map((n) => Number.parseInt(n, 10));
  const l = latest.split(".").map((n) => Number.parseInt(n, 10));
  if (!Number.isFinite(c[0]) || !Number.isFinite(l[0])) return "unknown";
  if ((c[0] ?? 0) < (l[0] ?? 0)) return "major";
  if ((c[0] ?? 0) > (l[0] ?? 0)) return "current";
  if ((c[1] ?? 0) < (l[1] ?? 0)) return "minor";
  if ((c[2] ?? 0) < (l[2] ?? 0)) return "patch";
  return "current";
}

export function VersionCell({ row, t }: { row: ScanListItem; t: T }) {
  if (!row.cms && !row.ecommerce) {
    return <span className="crm-mut text-[12.5px]">{t("scanner.table.noPlatform")}</span>;
  }

  const name = row.cmsName ?? row.ecommerceName ?? row.cms ?? "";
  const current = row.cmsVersion;
  const latest = row.cmsLatest;
  const gap = versionGap(current, latest);

  return (
    <span className="min-w-0">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="crm-trunc text-[13px]">{name}</span>
        {row.coreInsecure ? (
          <span title={t("scanner.table.insecureTitle")}>
            <AlertTriangle className="size-3.5 shrink-0 text-red-600" />
          </span>
        ) : null}
      </span>
      <span className="crm-prow-s crm-tnum flex items-center gap-1">
        <span className={cn(GAP_CLASS[gap])}>{current ?? "?"}</span>
        {latest ? (
          <>
            <span className="crm-mut">/</span>
            <span className="crm-mut">{latest}</span>
          </>
        ) : null}
        {gap !== "unknown" && gap !== "current" ? (
          <span className={cn("ml-0.5 rounded px-1 text-[10px]", GAP_CLASS[gap])}>{t(GAP_LABEL[gap])}</span>
        ) : null}
      </span>
    </span>
  );
}
